import type { BasketItem } from "~/widgets/stores/basket-store";
import type { BasketLine } from "~/widgets/lib/basket-lanes";

// What the class checkout puts in the basket, from what its flow hands over
// (pure, for the tests). One function for both lanes ("Add to basket" and
// "Book now"): each used to copy the fields one by one, so a field the flows
// set was silently dropped unless both lists named it.

/** A fresh checkout group: the server groups a checkout pass's lines by it
 *  (ticket rules, flat-price splitting). A multi-date pass shares one, set
 *  by the flow; a single add gets its own. */
export const newCheckoutGroupId = () =>
  `cg_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;

/** What the shared checkout flows hand over for a basket line (loosely
 *  typed there; the fields this reads). */
export type FlowItemData = Partial<BasketLine>;

export function bookingLine(
  itemData: FlowItemData,
  {
    businessId,
    schoolName,
    currency,
    offerRef,
  }: {
    businessId: string;
    schoolName: string;
    currency: string;
    /** A waiting-list offer being claimed (the offer email's `offer=`). */
    offerRef?: string;
  },
): BasketLine {
  const type = itemData.type || "booking";
  return {
    type,
    schoolId: businessId,
    schoolName: schoolName || businessId,
    courseId: itemData.courseId,
    courseName: itemData.courseName,
    sessionId: itemData.sessionId,
    sessionDate: itemData.sessionDate,
    // The LISTING's clock.
    timezone: itemData.timezone,
    bundleTemplateId: itemData.bundleTemplateId,
    bundleName: itemData.bundleName,
    bundleDescription: itemData.bundleDescription,
    bundleTotalSessions: itemData.bundleTotalSessions,
    bundleExpiresIn: itemData.bundleExpiresIn,
    bundleExpirationPeriodType: itemData.bundleExpirationPeriodType,
    ticketSelections: itemData.ticketSelections,
    addOns: itemData.addOns,
    bookingType: itemData.bookingType,
    isOnline: itemData.isOnline,
    appointmentData: itemData.appointmentData,
    // Each guest's details, as the hosted widget's basket keeps them: the
    // basket checkout sends them on with the line.
    guestDetails: itemData.guestDetails,
    checkoutGuestsInformation: itemData.checkoutGuestsInformation,
    userInputs: itemData.userInputs,
    participants: itemData.participants,
    quantity: 1,
    price: itemData.price ?? 0,
    currency: itemData.currency || currency,
    imageUrl: itemData.imageUrl,
    checkoutGroupId: itemData.checkoutGroupId || newCheckoutGroupId(),
    // A waiting-list claim: the offer's hold id on a date's booking (never
    // a bundle, as the hosted book-now page has it), so the basket's
    // validation doesn't count the claimer's own held seat against them
    // before their email is known.
    ...(offerRef && type !== "bundle" ? { offerRef } : {}),
  };
}

/**
 * The seats this visitor already holds in their basket for each date of a
 * class (by session), so the date picker takes them off "spots left" and a
 * one-seat date can't be added twice. The hosted widget's rule
 * (CheckoutOverlay's `reservedSessionQuantities`). Undefined when none.
 */
export function reservedSeats(
  items: readonly BasketItem[],
  courseId: string,
): Record<string, number> | undefined {
  const seats: Record<string, number> = {};
  for (const item of items) {
    if (item.type !== "booking" || item.courseId !== courseId || !item.sessionId) continue;
    const held = item.ticketSelections?.length
      ? item.ticketSelections.reduce((sum, t) => sum + (t.quantity || 0), 0)
      : item.quantity || 0;
    if (held <= 0) continue;
    seats[item.sessionId] = (seats[item.sessionId] ?? 0) + held;
  }
  return Object.keys(seats).length > 0 ? seats : undefined;
}
